import { FC, KeyboardEvent, ReactNode, useCallback, useState } from 'react'

import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  Text,
  IconMinimize,
  IconExpand,
  IconEdit,
  IconXMark,
  Button,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  Primary13Regular,
  Primary13SemiBold,
  RedRed50,
  SequenceMenu,
  TextFieldInput,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import {
  configFormExpandInteractedAtom,
  deleteNodeAtom,
  isConfigFormExpandedAtom,
  selectedNodeIdsAtom
} from 'Automations/automationEditor/atoms'
import { CONFIG_FORM_EXPAND_WIDTH_TRANSITION } from 'Automations/automationEditor/constants'
import { useNode } from '../hooks'

const Container: FC<{
  children: ReactNode
  leadingContent?: ReactNode
  minHeight?: string
  width?: string
  expandedWidth?: string
  padding?: string
  gap?: string
}> = ({
  children,
  leadingContent,
  minHeight,
  width = '456px',
  expandedWidth,
  padding = '8px',
  gap = '8px'
}) => {
  const isExpanded = useAtomValue(isConfigFormExpandedAtom)
  const hasInteracted = useAtomValue(configFormExpandInteractedAtom)
  const resolvedWidth = isExpanded && expandedWidth ? expandedWidth : width
  return (
    <Flex position="relative" height="100%">
      {leadingContent && (
        <Flex
          borderRight={`${GreyGrey30} 1px solid`}
          position="absolute"
          top="0"
          bottom="0"
          left="0"
          overflow="hidden"
        >
          {leadingContent}
        </Flex>
      )}
      <Flex
        // Only transition once the user has toggled expand, so the initial
        // width isn't animated on mount or when switching nodes.
        transition={
          hasInteracted ? CONFIG_FORM_EXPAND_WIDTH_TRANSITION : undefined
        }
        data-testid="automations.editor.configForm.panel"
        flexDir="column"
        gap={gap}
        width={resolvedWidth}
        padding={padding}
        minHeight={minHeight}
        marginLeft={leadingContent ? '185px' : undefined}
        height="100%"
      >
        {children}
      </Flex>
    </Flex>
  )
}

const Header: FC<{ nodeId: string; configActions?: ReactNode }> = ({
  nodeId,
  configActions
}) => {
  const { name, definition, nodeData, setNodeData } = useNode<{ name: string }>(
    { nodeId }
  )
  const setSelectedNodeIds = useSetAtom(selectedNodeIdsAtom)

  const [isEditing, setIsEditing] = useState(false)
  const [editingName, setEditingName] = useState('')

  const handleStartEdit = useCallback(() => {
    setEditingName(nodeData?.name ?? '')
    setIsEditing(true)
  }, [nodeData?.name])

  const handleSave = useCallback(() => {
    setNodeData('name', editingName)
    setIsEditing(false)
  }, [editingName, setNodeData])

  const handleCancel = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  if (!definition) return null

  const ConfigForm = definition.configForm
  if (!ConfigForm) return null

  const Icon = definition.icon

  return (
    <Flex justifyContent="space-between" gap="8px">
      <Flex
        flex="1"
        gap="8px"
        overflow="hidden"
        alignItems="center"
        role="group"
      >
        <Icon boxSize="32px" />
        {isEditing ? (
          <TextFieldInput
            data-testid="automations.editor.configForm.header.name"
            value={editingName}
            onChange={e => setEditingName(e.target.value)}
            autoFocus
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            maxLength={64}
            height="26px"
            placeholder={definition.name}
          />
        ) : (
          <>
            <Text
              data-testid="automations.editor.configForm.header.name"
              {...Primary13SemiBold}
              color={GreyGrey80}
              overflow="hidden"
              cursor="pointer"
              onClick={handleStartEdit}
            >
              {name}
            </Text>
            <Flex
              display="none"
              _groupHover={{ display: 'flex' }}
              alignItems="center"
              cursor="pointer"
              onClick={handleStartEdit}
            >
              <IconEdit color={GreyGrey60} />
            </Flex>
          </>
        )}
      </Flex>

      <Flex alignItems="center">
        <KebabMenu />
        {configActions}
        <Button
          data-testid="automations.editor.configForm.close"
          variant="ghost"
          onClick={() => setSelectedNodeIds([])}
          size="32px"
          icon={<IconXMark color={GreyGrey80} />}
          aria-label="Close"
        />
      </Flex>
    </Flex>
  )
}

const KebabMenu: FC = () => {
  const selectedNodeIds = useAtomValue(selectedNodeIdsAtom)
  const deleteNode = useSetAtom(deleteNodeAtom)
  const selectedNodeId = selectedNodeIds.at(0)

  const { definition } = useNode({ nodeId: selectedNodeId ?? '' })

  if (!definition || !!definition.isPersistent) return null

  return (
    <SequenceMenu
      data-testid="automations.editor.configForm.kebabMenu"
      variant="ghost"
      alignment="bottom-right"
      items={[
        <SequenceMenu.Item
          key="remove-node"
          uuid="remove-node"
          label="Remove node"
          data-testid="automations.editor.configForm.removeNode"
          style={{ color: RedRed50 }}
          onClick={() => {
            if (!selectedNodeId) return
            deleteNode(selectedNodeId)
          }}
        />
      ]}
    />
  )
}

const Row: FC<{ label: string; children: ReactNode; gap?: string }> = ({
  label,
  children,
  gap
}) => {
  return (
    <Grid gridTemplateColumns="120px auto">
      <GridItem paddingX="4px" paddingY="8px">
        <Text {...Primary13Regular} color={GreyGrey70}>
          {label}
        </Text>
      </GridItem>
      <GridItem as={Flex} flexDir="column" width="100%" minWidth="0" gap={gap}>
        {children}
      </GridItem>
    </Grid>
  )
}

const Footer: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Flex marginTop="auto" paddingTop="8px" gap="8px">
      {children}
    </Flex>
  )
}

const ExpandToggle: FC = () => {
  const [isExpanded, setIsExpanded] = useAtom(isConfigFormExpandedAtom)
  const setHasInteracted = useSetAtom(configFormExpandInteractedAtom)
  const Icon = isExpanded ? IconMinimize : IconExpand
  return (
    <Button
      data-testid="automations.editor.configForm.expandToggle"
      variant="ghost"
      onClick={() => {
        setHasInteracted(true)
        setIsExpanded(v => !v)
      }}
      size="32px"
      icon={<Icon color={GreyGrey80} />}
      aria-label={isExpanded ? 'Collapse' : 'Expand'}
    />
  )
}

const Separator: FC = () => (
  <Box width="100%" height="1px" backgroundColor={GreyGrey30} />
)

export const ConfigFormTemplate = Object.assign(Container, {
  Header,
  Row,
  Footer,
  ExpandToggle,
  Separator
})
